const LOGIN_NEXT_STORAGE_KEY = "kovaad_post_login_next";

const DASHBOARD_ROLES = new Set(["guardian", "admin", "center_head"]);

/** In-memory fallback when sessionStorage is unavailable (SSR / some tests). */
let memoryLoginNext: string | null = null;

function normalizeRole(role?: string | null): string {
  return role?.trim().toLowerCase().replace(/\s+/g, "_") ?? "";
}

/** Same-origin dashboard paths only (open-redirect safe). */
export function isAllowlistedLoginNext(
  nextParam: string | null | undefined,
): boolean {
  if (nextParam == null || nextParam === "") return false;
  const next = nextParam.trim();
  if (
    next.startsWith("//") ||
    next.startsWith("http://") ||
    next.startsWith("https://") ||
    next.includes("..") ||
    !next.startsWith("/dashboard/")
  ) {
    return false;
  }
  return true;
}

/** Persist intended post-login path across auth redirects / SSO. */
export function stashLoginNext(nextParam: string | null | undefined): void {
  if (!isAllowlistedLoginNext(nextParam)) return;
  const value = (nextParam as string).trim();
  memoryLoginNext = value;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(LOGIN_NEXT_STORAGE_KEY, value);
  } catch {
    // ignore quota / private mode
  }
}

function readStashedLoginNext(): string | null {
  if (typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage.getItem(LOGIN_NEXT_STORAGE_KEY);
      if (stored) return stored;
    } catch {
      // ignore
    }
  }
  return memoryLoginNext;
}

export function clearStashedLoginNext(): void {
  memoryLoginNext = null;
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LOGIN_NEXT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Prefer ?next= from the URL; fall back to sessionStorage (survives login redirects).
 * Clears the stash after a successful allowlisted read.
 */
export function resolvePostLoginPath(
  defaultPath: string,
  nextParam: string | null | undefined,
  role?: string,
): string {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole || !DASHBOARD_ROLES.has(normalizedRole)) {
    return defaultPath;
  }

  const fromQuery =
    nextParam != null && nextParam !== "" ? nextParam.trim() : null;
  const fromStorage = readStashedLoginNext();
  const candidate = fromQuery || fromStorage;

  if (!isAllowlistedLoginNext(candidate)) {
    return defaultPath;
  }

  clearStashedLoginNext();
  return candidate as string;
}
